import type { IAnvändare } from 'src/types/nya/common';
import type { NavGroupProps, NavItemDataProps, NavSectionProps } from 'src/components/nav-section';

// ----------------------------------------------------------------------

/**
 * Behörighetsgrupperna som sidebaren filtrerar på. Detta är medvetet ett annat
 * begrepp än backendens `RollNamn`: flera roller ger samma behörighet, och en
 * roll kan ge flera. Mappningen sker i {@link ROLL_TILL_BEHÖRIGHETER}.
 *
 * Typen är en union — inte `string` — så att en felstavning i `allowedRoles`
 * fångas av kompilatorn i stället för att tyst dölja en meny.
 */
export type Behörighet =
  | 'alla'
  | 'boende'
  | 'medlem'
  | 'styrelseledamot'
  | 'organisation_admin'
  | 'ekonomi'
  | 'egenföretagare'
  | 'arborist'
  | 'super_admin';

/**
 * Namngivna uppsättningar att använda i trädet i stället för att upprepa
 * samma arrayer. `BOENDE` räcker för allt som en vanlig medlem ska nå —
 * styrelse och admin ärver den behörigheten via mappningen nedan.
 */
export const BEHÖRIGHET = {
  ALLA: ['alla'],
  BOENDE: ['boende'],
  MEDLEM: ['medlem'],
  STYRELSE: ['styrelseledamot'],
  EKONOMI: ['ekonomi'],
  ADMIN: ['organisation_admin'],
  KONSULT: ['egenföretagare'],
  ARBORIST: ['arborist'],
  SUPER_ADMIN: ['super_admin'],
} satisfies Record<string, Behörighet[]>;

/**
 * Navigeringsträdets posttyp. Identisk med {@link NavItemDataProps} så när som
 * på `allowedRoles`, som här är {@link Behörighet}[] i stället för `string[]` —
 * det är den smalare typen som gör att en felstavad behörighet inte kompilerar.
 * Trädet är strukturellt kompatibelt med `NavSectionProps['data']` åt andra
 * hållet, så nav-komponenterna behöver inte känna till den här typen.
 */
export type NavPost = Omit<NavItemDataProps, 'allowedRoles' | 'children'> & {
  allowedRoles?: Behörighet[];
  children?: NavPost[];
};

export type NavData = { subheader?: string; items: NavPost[] }[];

/**
 * Backendens roller (gemener) → behörighetsgrupper. Varje rad listar hela den
 * transitiva mängden, så uppslagningen aldrig behöver följa kedjor.
 *
 * Observera att styrelseroller också ger `boende`/`medlem`: en styrelseledamot i
 * en BRF är per definition medlem och ska nå tvättstuga och utlåningspool som
 * alla andra. `förvaltare` är undantaget — den bor inte i föreningen.
 */
const ROLL_TILL_BEHÖRIGHETER: Record<string, Behörighet[]> = {
  medlem: ['boende', 'medlem'],
  ekonomisk_förening_medlem: ['boende', 'medlem'],
  samfällighetsmedlem: ['boende', 'medlem'],
  blivande_medlem: ['boende'],
  hyresgäst: ['boende'],
  andrahandshyresgäst: ['boende'],
  styrelsemedlem: ['boende', 'medlem', 'styrelseledamot', 'ekonomi'],
  styrelseledamot: ['boende', 'medlem', 'styrelseledamot', 'ekonomi'],
  förvaltare: ['styrelseledamot', 'ekonomi'],
  kassör: ['ekonomi'],
  ekonomiassistent: ['ekonomi'],
  organisation_admin: ['boende', 'medlem', 'styrelseledamot', 'organisation_admin', 'ekonomi'],
  egenföretagare: ['egenföretagare', 'ekonomi'],
  aktiebolagsägare: ['egenföretagare', 'ekonomi'],
  arborist: ['arborist'],
  super_admin: [
    'boende',
    'medlem',
    'styrelseledamot',
    'organisation_admin',
    'ekonomi',
    'egenföretagare',
    'arborist',
    'super_admin',
  ],
};

// ----------------------------------------------------------------------

/**
 * Ger föräldrar utan egen `allowedRoles` unionen av barnens. Poängen är att en
 * gren aldrig ska kunna bli oåtkomlig för att någon glömt uppdatera föräldern
 * när ett barn öppnats för fler roller.
 *
 * Sätter man `allowedRoles` på en förälder blir den i stället en hård grind:
 * barnen filtreras bara om föräldern släpper igenom. Använd det medvetet, t.ex.
 * på administrationsmenyerna.
 */
export function härledBehörigheter(items: NavPost[]): NavPost[] {
  return items.map((item) => {
    if (!item.children?.length) {
      return item;
    }

    const barn = härledBehörigheter(item.children);

    if (item.allowedRoles?.length) {
      return { ...item, children: barn };
    }

    const union = new Set<Behörighet>();
    barn.forEach((b) => b.allowedRoles?.forEach((roll) => union.add(roll)));

    return { ...item, allowedRoles: [...union], children: barn };
  });
}

/**
 * Om en meny ska visas för organisationen.
 *
 * Saknar posten nyckel är den alltid synlig. Finns en sparad inställning styr
 * den. Saknas inställningen faller vi tillbaka på postens eget utgångsläge:
 * synlig som standard, om den inte är märkt {@link NavItemDataProps.standardDold}
 * — då krävs att organisationen uttryckligen slår på den.
 */
export function ärMenySynlig(
  menyNyckel: string | undefined,
  menySynlighet?: Record<string, boolean>,
  standardDold = false
): boolean {
  if (!menyNyckel) {
    return true;
  }

  const sparad = menySynlighet?.[menyNyckel];

  return sparad === undefined ? !standardDold : sparad !== false;
}

function harBehörighet(allowedRoles: string[] | undefined, effektiva: Set<string>): boolean {
  if (allowedRoles?.some((roll) => roll === 'alla')) {
    return true;
  }
  if (!allowedRoles?.length) {
    return false;
  }
  return allowedRoles.some((roll) => effektiva.has(roll.toLowerCase()));
}

/**
 * Expanderar en lista roller till alla behörighetsgrupper de ger.
 *
 * Tar `unknown` därför att rollernas form varierar mellan kodvägar: vissa
 * levererar objekt med `namn`, andra med `rollNamn`, och några rena strängar.
 * Den varianten var orsaken till en tyst bugg i {@link ärOrganisationsadmin} —
 * en jämförelse mot objektet i stället för namnet returnerar alltid falskt.
 */
export function expanderaRoller(roller: unknown): Set<string> {
  const behörigheter = new Set<string>();

  if (!Array.isArray(roller)) {
    return behörigheter;
  }

  for (const roll of roller) {
    const rollNamn = String(
      (roll as any)?.rollNamn ?? (roll as any)?.namn ?? roll ?? ''
    ).toLowerCase();
    if (!rollNamn) continue;
    behörigheter.add(rollNamn);
    ROLL_TILL_BEHÖRIGHETER[rollNamn]?.forEach((b) => behörigheter.add(b));
  }

  return behörigheter;
}

/**
 * Sant om rollerna ger den efterfrågade behörighetsgruppen.
 *
 * Det här är den enda källan till sanning för "får den här personen göra X" i
 * gränssnittet. Funktioner som bygger egna rollistor hamnar förr eller senare i
 * otakt med {@link ROLL_TILL_BEHÖRIGHETER} — vilket är precis vad som hände i
 * utlåningspoolen, där sidomenyn släppte in en styrelseledamot på en sida där
 * varje knapp sedan var admin-spärrad.
 */
export function harBehörighetsgrupp(roller: unknown, grupp: Behörighet): boolean {
  return expanderaRoller(roller).has(grupp);
}

/**
 * Expanderar användarens roller till alla behörighetsgrupper de ger.
 */
function effektivaBehörigheter(anvandare: IAnvändare): Set<string> {
  return expanderaRoller(anvandare.roller);
}

export const ROLLER_FÖR_MENYINSTÄLLNINGAR: { kod: Behörighet; etikett: string }[] = [
  { kod: 'alla', etikett: 'Alla' },
  { kod: 'boende', etikett: 'Boende & Medlemmar' },
  { kod: 'styrelseledamot', etikett: 'Styrelseledamöter' },
  { kod: 'ekonomi', etikett: 'Ekonomiansvariga' },
  { kod: 'organisation_admin', etikett: 'Organisationsadmin' },
  { kod: 'egenföretagare', etikett: 'Konsulter & Egenföretagare' },
  { kod: 'arborist', etikett: 'Arborist' },
];

export type MenyInställningObjekt = {
  synlig: boolean;
  roller?: string[];
};

export type MenyInställningInput = Record<string, boolean | MenyInställningObjekt>;

function filtreraPost(
  item: NavItemDataProps,
  effektiva: Set<string>,
  menySynlighet?: MenyInställningInput
): NavItemDataProps | null {
  const menyVal = item.menyNyckel ? menySynlighet?.[item.menyNyckel] : undefined;
  
  let synlig = true;
  let tillåtnaRoller = item.allowedRoles;

  if (typeof menyVal === 'boolean') {
    synlig = menyVal;
  } else if (menyVal && typeof menyVal === 'object') {
    synlig = menyVal.synlig !== false;
    if (menyVal.roller && menyVal.roller.length > 0) {
      tillåtnaRoller = menyVal.roller;
    }
  } else {
    synlig = ärMenySynlig(item.menyNyckel, undefined, item.standardDold);
  }

  if (!synlig) {
    return null;
  }

  if (!harBehörighet(tillåtnaRoller, effektiva)) {
    return null;
  }

  const nyPost = { ...item };

  if (nyPost.children) {
    const kvarvarande = nyPost.children
      .map((barn) => filtreraPost(barn, effektiva, menySynlighet))
      .filter((barn): barn is NavItemDataProps => barn !== null);

    if (kvarvarande.length > 0) {
      nyPost.children = kvarvarande;
    } else if (!nyPost.path) {
      // Ren mapp utan kvarvarande innehåll — dölj den.
      return null;
    } else {
      // Egen länk kvar: behåll den som löv.
      delete nyPost.children;
    }
  }

  return nyPost;
}

/**
 * Behörighetskontrollen som nav-komponenterna får via `checkPermissions`.
 *
 * Nav-komponenterna filtrerar posterna en gång till när de renderar, ovanpå
 * {@link filtreraNavigering}. Den kontrollen måste använda exakt samma
 * rollexpansion — gör den en rå jämförelse mot rollnamnen försvinner varje post
 * vars `allowedRoles` är en behörighetsgrupp (`boende`, `ekonomi`) i stället för
 * ett rollnamn.
 */
export function skapaBehörighetskontroll(
  anvandare: IAnvändare | undefined
): (allowedRoles?: string[]) => boolean {
  const effektiva = anvandare ? effektivaBehörigheter(anvandare) : new Set<string>();
  return (allowedRoles) => harBehörighet(allowedRoles, effektiva);
}

/**
 * Filtrerar navigeringsträdet mot användarens roller och organisationens
 * menyinställningar. Tomma sektioner faller bort.
 */
export function filtreraNavigering(
  data: NavSectionProps['data'],
  anvandare: IAnvändare | undefined,
  menySynlighet?: MenyInställningInput
): NavGroupProps[] {
  if (!anvandare?.namn) {
    return [];
  }

  const effektiva = effektivaBehörigheter(anvandare);

  return data
    .map((grupp) => {
      const poster = grupp.items
        .map((item) => filtreraPost(item, effektiva, menySynlighet))
        .filter((item): item is NavItemDataProps => item !== null);

      return poster.length > 0 ? { ...grupp, items: poster } : null;
    })
    .filter((grupp): grupp is NavGroupProps => grupp !== null);
}

// ----------------------------------------------------------------------

export type MenyKatalogPost = {
  nyckel: string;
  etikett: string;
  sektion: string;
  /** Utgångsläget när organisationen inte sparat något val för menyn. */
  standardSynlig: boolean;
  standardRoller: Behörighet[];
};

export type MenyKatalogNod = MenyKatalogPost & {
  barn?: MenyKatalogNod[];
};

/**
 * Bygger en hierarkisk struktur av menykatalogen ur trädet.
 */
export function byggMenyTräd(data: NavSectionProps['data']): MenyKatalogNod[] {
  const samlaNod = (item: NavItemDataProps, sektion: string): MenyKatalogNod | null => {
    const barn = (item.children ?? [])
      .map((child) => samlaNod(child, sektion))
      .filter((child): child is MenyKatalogNod => child !== null);

    if (!item.menyNyckel && barn.length === 0) {
      return null;
    }

    // Om posten saknar egen nyckel men har barn med nycklar (t.ex. en gruppmapp),
    // kan vi använda en tillfällig nyckel eller returnera barnen.
    if (item.menyNyckel) {
      return {
        nyckel: item.menyNyckel,
        etikett: item.title,
        sektion,
        standardSynlig: !item.standardDold,
        standardRoller: (item.allowedRoles as Behörighet[]) ?? [],
        ...(barn.length > 0 ? { barn } : {}),
      };
    }

    return null;
  };

  return data.flatMap((grupp) => {
    const sektion = grupp.subheader ?? 'Allmänt';
    return grupp.items
      .map((item) => samlaNod(item, sektion))
      .filter((nod): nod is MenyKatalogNod => nod !== null);
  });
}

/**
 * Härleder katalogen över av/på-bara menyer direkt ur trädet i stället för att
 * upprepa den i en handunderhållen lista. Sektionen är gruppens rubrik; poster i
 * den rubriklösa startgruppen hamnar under "Allmänt".
 */
export function byggMenyKatalog(data: NavSectionProps['data']): MenyKatalogPost[] {
  const platta = (noder: MenyKatalogNod[]): MenyKatalogPost[] =>
    noder.flatMap((nod) => [
      {
        nyckel: nod.nyckel,
        etikett: nod.etikett,
        sektion: nod.sektion,
        standardSynlig: nod.standardSynlig,
        standardRoller: nod.standardRoller,
      },
      ...platta(nod.barn ?? []),
    ]);

  return platta(byggMenyTräd(data));
}
